import { useState, useEffect } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useStore } from '../../../store'
import { useThemeStore } from '../../../store/ConfigStore';
import { useToast } from '@/components/Toast';
import { Table, Drawer, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { MobileTableCard } from '@/components/MobileCards';
import type { Dayjs } from 'dayjs';
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import type { SuperAgent } from '@/types/customerManagement';
import { fetchAgentID, fetchGroupID, fetchSuperAgentList, upsertSuperAgent } from '@/services/customerManagement';
import { Pagination } from "@/components/Pagination";
import dayjs from 'dayjs';
import CopyIcon from '../../../assets/svg/copy.svg';
import CopyDarkIcon from '../../../assets/svg/copyDark.svg';
import CheckIcon from '../../../assets/svg/check-line.svg';
import CheckDarkIcon from '../../../assets/svg/check-line-dark.svg';


export const routeConfig: RouteConfig = {
    title: 'route.customerManagement.superAgentSystem',
    layout: true,
    sort: 2,
    hidden: true,
    hiddenInMenu: true
};

// interface SuperAgent {
//     superAgentUID?: string;
//     timeToBecomeSuperAgent: number,
//     invitationCode: string,
//     registrationTime: number,
//     totalInvites: number,
//     agentCount: number,
//     rebateRatio: number ,
//     operation?: React.ReactNode;
// }

// interface SuperAgentDetails {
//     superAgentUID: string;
//     username?: string;
//     spotRatio?: number;
//     spotMyRatio?: number;
//     futuresRatio?: number;
//     futuresMyRatio?: number;
//     alphaRatio?: number;
//     alphaMyRatio?: number;
//     groupName?: string;
//     timeToBecomeSuperAgent?: number; // time to become super agent in unix timestamp
// }

const SuperAgent = () => {
    const { t } = useLocale();
    const { UserStore } = useStore();
    const isDark = useThemeStore();
    const toast = useToast();

    // const [selectedUID, setSelectedUID] = useState(0);
    const [UID, setUID] = useState('');
    const [customerGroups, setCustomerGroups] = useState<{ groupId: string, groupName: string }[]>([]);
    const [selectedCustomerGroup, setSelectedCustomerGroup] = useState(0);
    const [timeToBecomeSuperAgent, setTimeToBecomeSuperAgent] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(1689081600);
    const [superAgentListData, setSuperAgentListData] = useState<SuperAgent[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);
    const [copiedUID, setCopiedUID] = useState<string | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    
    // For managing client drawer
    const [isCreate, setIsCreate] = useState(false);
    const [allCustomerUID, setAllCustomerUID] = useState<{ index: number, uid: string }[]>([]);
    const [selectedUID, setSelectedUID] = useState(0);
    const [selectedRecord, setSelectedRecord] = useState<SuperAgent | null>(null);
    const [maxSpotRatio, setMaxSpotRatio] = useState<number>(0);
    const [maxFuturesRatio, setMaxFuturesRatio] = useState<number>(0);

    // Helper to build params for super agent list API
    const buildSuperAgentParams = (UID: string, selectedCustomerGroup: number, timeToBecomeSuperAgent: [Dayjs | null, Dayjs | null]) => {
        let params: any = {};
        if (UID !== '') {
            params.agent_id = UID;
        }
        if (selectedCustomerGroup !== 0) {
            params.group_id = selectedCustomerGroup;
        }
        if (timeToBecomeSuperAgent[0] && timeToBecomeSuperAgent[1]) {
            params.time_range = [timeToBecomeSuperAgent[0].unix(), timeToBecomeSuperAgent[1].unix()];
        }
        return params;
    };

    const handleReset = () => {
        try {
            // setSelectedUID(0)
            setUID('');
            setSelectedCustomerGroup(0);
            setTimeToBecomeSuperAgent([null, null]);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            fetchSuperAgentList({}, 1, pagination.pageSize).then(res => {
                setSuperAgentListData(res.list);
                setTotal(res.total);
            });
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.superAgent.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = () => {
        const params = buildSuperAgentParams(UID, selectedCustomerGroup, timeToBecomeSuperAgent);
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
        fetchSuperAgentList(params, 1, pagination.pageSize).then(res => {
            setSuperAgentListData(res.list);
            setTotal(res.total);
        });
    };

    const handleCreateClick = async () => {
        setSelectedRecord(null);
        setSelectedUID(0);
        setIsCreate(true);
        setDrawerOpen(true);

        try {
            const allUIDs = await fetchAgentID('');
            // Map string[] to { index: number, uid: string }[]
            const mappedUIDs = allUIDs.map((uid: string, idx: number) => ({ index: idx + 1, uid }));
            setAllCustomerUID(mappedUIDs);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.superAgent.fetchError') || 'An error occurred while fetching customer UIDs.'
            });
        }
    };

    const handleManageClick = (record: SuperAgent) => {
        // Find the corresponding details
        if (record) {
            setSelectedRecord({ ...record });
            setDrawerOpen(true);
        }
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setIsCreate(false);
        setSelectedRecord(null);
    };
    
    // need to add create save
    const handleDrawerSave = async () => {
        if (!selectedRecord) {
            toast.error({
                message: t('customerManagement.superAgent.noRecordSelected') || 'No record selected.'
            });
            return;
        }

        // Find the original record for comparison
        const original = superAgentListData.find(item => item.superAgentUID === selectedRecord.superAgentUID);
        if (original) {
            // Only allow increasing ratios
            if (
                (selectedRecord.spotRatio ?? 0) < (original.spotRatio ?? 0) ||
                (selectedRecord.rebateRatio ?? 0) < (original.rebateRatio ?? 0)
            ) {
                setDrawerOpen(false);
                setIsCreate(false);
                setSelectedRecord(null);
                setTimeout(() => {
                    toast.error({
                        message: t('customerManagement.superAgent.modifyRatioIncreaseOnly') || 'You can only increase the ratios.'
                    });
                }, 200);
                return;
            }
        }

        try {
            await upsertSuperAgent({
                agent_id: selectedRecord.superAgentUID,
                reward_ratio: selectedRecord.rebateRatio,
                spot_ratio: selectedRecord.spotRatio,
                user_name: selectedRecord.username,
            });

            const params = buildSuperAgentParams(UID, selectedCustomerGroup, timeToBecomeSuperAgent);
            try {
                const res = await fetchSuperAgentList(params, 1, pagination.pageSize);
                setSuperAgentListData(res.list);
                setTotal(res.total);
            } catch (fetchError: any) {
                toast.error({
                    message: fetchError?.message || t('customerManagement.superAgent.fetchError') || 'An error occurred while refreshing client list.'
                });
            }

            toast.success({
                message: t('customerManagement.superAgent.modifySuccess') || 'Saved successfully.'
            });
        } catch (err: any) {
            toast.error({
                message: err?.message || t('customerManagement.superAgent.saveError') || 'An error occurred while saving.'
            });
        } finally {
            setDrawerOpen(false);
            setIsCreate(false);
            setSelectedRecord(null);
        }
    };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('customerManagement.superAgent.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const handleCopyUID = async (uid: string) => {
        try {
            await navigator.clipboard.writeText(uid);
            setCopiedUID(uid);
            setTimeout(() => setCopiedUID(null), 2000);
            toast.success({ message: t('customerManagement.superAgent.copySuccess') || 'UID copied!' });
        } catch (err: any) {
            toast.error({ message: err?.message || t('customerManagement.superAgent.copyError') || 'Failed to copy UID.' });
        }
    };

    const queryFields: QueryField[] = [
        // {
        //     type: 'preTabInput',
        //     name: 'selectedUID',
        //     label: "UID",
        //     prefixOptions: [
        //         { value: null, label: t('customerManagement.superAgent.customerUID') },
        //         { value: 0, label: t('customerManagement.superAgent.superiorAgent') },
        //     ],
        //     prefixValue: selectedUID,
        //     onPrefixChange: (value) => setSelectedUID(value),
        //     value: UID,
        //     onChange: setUID,
        //     placeholder: t('customerManagement.superAgent.pleaseEnterPlaceholder'),
        //     colSpan: { mobile: 2, desktop: 2 }
        // },
        {
            type: 'input',
            name: 'UID',
            label: t('customerManagement.superAgent.superAgentUID'),
            value: UID,
            onChange: setUID,
            placeholder: t('customerManagement.superAgent.pleaseEnterPlaceholder'),
        },
        {
            type: 'select',
            name: 'customerGroup',
            label: t('customerManagement.superAgent.customerGroup'),
            value: selectedCustomerGroup,
            onChange: setSelectedCustomerGroup,
            options: [
                // get from client group 
                { value: null, label: t('customerManagement.superAgent.all') },
                ...customerGroups.map(group => ({ value: parseInt(group.groupId), label: group.groupName }))
            ]
        },
        {
            type: 'dateRange',
            name: 'timeToBecomeSuperAgent',
            label: t('customerManagement.superAgent.table.timeToBecomeSuperAgent'),
            value: timeToBecomeSuperAgent,
            onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
                setTimeToBecomeSuperAgent(dates ?? [null, null]);
            },
            placeholder: [t('customerManagement.superAgent.startTime'), t('customerManagement.superAgent.endTime')],
        }
    ];

    const superAgentListColumns: ColumnsType<SuperAgent> = [
        {
            title: t('customerManagement.superAgent.table.superAgentUID'),
            dataIndex: 'superAgentUID',
            key: 'superAgentUID',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text: string) => (
                <div className="flex items-center gap-1">
                    <span>{text}</span>
                    <button
                        className="border-none bg-transparent p-0 m-0"
                        onClick={() => handleCopyUID(text)}
                    >
                        <img
                            src={copiedUID === text ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)}
                            alt={copiedUID === text ? 'copied' : 'copy'}
                            className="w-4 h-4"
                        />
                    </button>
                </div>
            ),
        },
        {
            title: t('customerManagement.superAgent.table.timeToBecomeSuperAgent'),
            dataIndex: 'timeToBecomeSuperAgent',
            key: 'timeToBecomeSuperAgent',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => formatDate(text, true)
        },
        {
            title: t('customerManagement.superAgent.table.invitationCode'),
            dataIndex: 'invitationCode',
            key: 'invitationCode',
            align: 'start',
            width: 120,
            ellipsis: true
        },
        {
            title: t('customerManagement.superAgent.table.registrationTime'),
            dataIndex: 'registrationTime',
            key: 'registrationTime',
            align: 'start',
            width: 140,
            ellipsis: true,
            render: (text) => formatDate(text, false)
        },
        {
            title: t('customerManagement.superAgent.table.totalInvites'),
            dataIndex: 'totalInvites',
            key: 'totalInvites',
            align: 'start',
            width: 100,
            ellipsis: true
        },
        {
            title: t('customerManagement.superAgent.table.agentCount'),
            dataIndex: 'agentCount',
            key: 'agentCount',
            align: 'start',
            width: 100,
            ellipsis: true
        },
        {
            title: t('customerManagement.superAgent.table.rebateRatio'),
            dataIndex: 'rebateRatio',
            key: 'rebateRatio',
            align: 'start',
            width: 100,
            ellipsis: true,
            render: (text) => <span>{text}%</span>
        },
        {
            title: t('customerManagement.superAgent.table.operation'),
            dataIndex: 'operation',
            key: 'operation',
            align: 'end',
            width: 110,
            fixed: 'right',
            render: (_, record) => (
                <div className="grid grid-cols-3 md:flex items-center justify-start md:justify-end gap-2">
                    <button
                        className="text-sm font-normal text-special-text font-inter w-14 bg-transparent border-none flex justify-center items-center"
                        onClick={() => handleManageClick(record)}
                    >
                        {t('customerManagement.superAgent.table.manage')}
                    </button>
                </div>
            )
        }
    ];

    // Prepare columns for mobile card (operation column marked)
    const mobileSuperAgentListColumns = superAgentListColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
        );

    // Sample data for demonstration
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                setMaxSpotRatio(UserStore.userSpotRatio); // Example max spot ratio
                setMaxFuturesRatio(UserStore.userRewardRatio); // Example max futures ratio

                try {
                    const groupRes = await fetchGroupID({});
                    setCustomerGroups(groupRes);
                } catch (groupError: any) {
                    toast.error({
                        message: groupError?.message || t('customerManagement.superAgent.groupFetchError') || 'An error occurred while fetching group list.'
                    });
                }

                const params = buildSuperAgentParams(UID, selectedCustomerGroup, timeToBecomeSuperAgent);
                fetchSuperAgentList(params, pagination.current, pagination.pageSize).then(res => {
                    setSuperAgentListData(res.list);
                    setTotal(res.total);
                });
                setLastUpdated(Math.floor(Date.now() / 1000));
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('customerManagement.superAgent.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.current, pagination.pageSize]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <GenericQueryCard
                title={t('customerManagement.superAgent.title')}
                subtitle={t('customerManagement.superAgent.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                fields={queryFields}
                onReset={handleReset}
                onQuery={handleQuery}
                gridCols="grid-cols-2 md:grid-cols-4"
            />
            
            {/* Table Container with Custom Layout */}
            <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                {/* Table Content */}
                <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                    <Table
                        dataSource={superAgentListData}
                        columns={superAgentListColumns}
                        rowKey="superAgentUID"
                        pagination={false}
                        loading={loading}
                        size="small"
                        // scroll={{ x: 1100 }}
                        className="custom-action-table"
                        showHeader={true}
                        locale={{ emptyText: '' }}
                        components={{
                            body: {
                                wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                            }
                        }}
                    />
                </div>

                <div className="block md:hidden">
                    {superAgentListData.map((row) => (
                        <MobileTableCard
                            key={row.superAgentUID}
                            record={row}
                            columns={mobileSuperAgentListColumns}
                            t={t}
                        />
                    ))}
                </div>

                <div className="mt-4 flex justify-end">
                    <Pagination
                        currentPage={pagination.current}
                        totalPages={Math.ceil(total / pagination.pageSize)}
                        onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                        pageSize={pagination.pageSize}
                        setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                        pageSizes={[10, 20, 50, 100]}
                        isPageSizeVisible={true}
                        disabled={loading}
                    />
                </div>
            </div>

            <Drawer
                title={
                    <div className="text-sel-normal dark:text-dark-sel-normal text-xl font-normal font-inter flex justify-between">
                        <div>
                            {isCreate ? t('customerManagement.superAgent.manage.addTitle') : t('customerManagement.superAgent.manage.manageTitle')}
                        </div>
                        <svg 
                            onClick={handleDrawerClose} 
                            className="cursor-pointer text-chart-title-text hover:text-white transition-colors" 
                            width="24" 
                            height="24" 
                            viewBox="0 0 24 24" 
                            fill="none"
                        >
                            <path 
                                d="M18 6L6 18M6 6l12 12" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                            />
                        </svg>
                    </div>
                }
                placement="right"
                size="large"
                closeIcon={false}
                onClose={handleDrawerClose}
                open={drawerOpen}
                zIndex={30}
                styles={{
                    header: {
                        background: '#16182E',
                        borderBottom: '0px',
                        padding: '20px 24px'
                    },
                    body: {
                        background: '#16182E',
                        padding: '24px'
                    }
                }}
            >
                {(() => {
                    // Use a local record for form fields, fallback to blank for create
                    const record = selectedRecord || { 
                        superAgentUID: 0, timeToBecomeSuperAgent: '', username: '', spotRatio: 0, rebateRatio: 0 };
                    return (
                        <div className="space-y-6">
                            {/* Client UID Section */}
                            <div className="space-y-[6px]">
                                <div className="flex items-start gap-[6px]">
                                    <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.superAgent.manage.UID')}</span>
                                </div>
                                {isCreate ? (
                                    <input
                                        type="text"
                                        placeholder={t('customerManagement.clientGroup.create.groupNameMaxLength')}
                                        className="bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter leading-none"
                                        maxLength={12}
                                        onChange={e => setSelectedRecord(r => r ? { ...r, superAgentUID: e.target.value } : r)}
                                        value={record.superAgentUID}
                                    />
                                ) : (

                                    <div className="bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px]">
                                        <span className="text-normal dark:text-dark-normal text-xs font-medium font-inter leading-none">{record.superAgentUID}</span>
                                    </div>
                                )}
                            </div>

                            {/* Username Section */}
                            <div className="space-y-[6px]">
                                <div className="flex items-start gap-[6px]">
                                    <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.superAgent.manage.username')}</span>
                                </div>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder={t('customerManagement.superAgent.manage.usernamePlaceholder')}
                                        className="bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter"
                                        maxLength={30}
                                        onChange={e => setSelectedRecord(r => r ? { ...r, username: e.target.value } : r)}
                                        value={selectedRecord?.username || ''}
                                    />
                                    <span className="absolute right-3 top-[15px] text-chart-title-text text-sm font-medium font-sans leading-tight">{(selectedRecord?.username || '').length}/30</span>
                                </div>
                            </div>

                            {/* Commission Settings Section */}
                            <div className="flex-col">
                                <div className="flex items-start gap-[6px] mb-[6px]">
                                    <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.superAgent.manage.commissionSettings')}</span>
                                </div>

                                {/* Spot Trading */}
                                <div className="space-y-3 mb-4 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.superAgent.manage.spot')}</span>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            placeholder={t('customerManagement.superAgent.manage.clientRatio')}
                                            suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                            className="flex flex-1 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                            onChange={e => {
                                                let value = e.target.value.replace(/\D/g, '');
                                                let num = Number(value);
                                                if (num > maxSpotRatio) num = maxSpotRatio;
                                                setSelectedRecord(r => r ? { ...r, spotRatio: num, spotMyRatio: (maxSpotRatio - num) } : r);
                                            }}
                                            value={selectedRecord?.spotRatio ?? 0}
                                        />
                                        <div>+</div>
                                        <Input
                                            placeholder={t('customerManagement.superAgent.manage.clientRatio')}
                                            suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                            className="flex flex-1 bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                            // value={MAX_PERCENTAGE - (selectedRecord?.spotRatio ?? 0)}
                                            value={maxSpotRatio - (selectedRecord?.spotRatio ?? 0)}
                                            disabled={true}
                                        />
                                        <div>=</div>
                                        <span className="text-special-text text-sm font-medium font-sans leading-tight">{maxSpotRatio}%</span>
                                    </div>
                                </div>

                                {/* Futures Trading */}
                                <div className="space-y-3 mb-4 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                                    <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.superAgent.manage.futures')}</span>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            placeholder={t('customerManagement.superAgent.manage.clientRatio')}
                                            suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                            className="flex flex-1 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                            onChange={e => {
                                                let value = e.target.value.replace(/\D/g, '');
                                                let num = Number(value);
                                                if (num > maxFuturesRatio) num = maxFuturesRatio;
                                                setSelectedRecord(r => r ? { ...r, rebateRatio: num } : r);
                                            }}
                                            value={selectedRecord?.rebateRatio ?? 0}
                                        />
                                        <div>+</div>
                                        <Input
                                            placeholder={t('customerManagement.superAgent.manage.myRatio')}
                                            suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                            className="flex flex-1 bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                            // value={MAX_PERCENTAGE - (selectedRecord?.futuresRatio ?? 0)}
                                            value={maxFuturesRatio - (selectedRecord?.rebateRatio ?? 0)}
                                            disabled={true}
                                        />
                                        <div>=</div>
                                        <span className="text-special-text text-sm font-medium font-sans leading-tight">{maxFuturesRatio}%</span>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-2 pt-6 pb-6 px-6">
                                <button
                                    onClick={handleDrawerClose}
                                    className="bg-transparent border border-dark-main dark:border-main opacity-60 rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                                >
                                    {t('customerManagement.superAgent.manage.cancel')}
                                </button>
                                <button
                                    onClick={() => {
                                        console.log('Save client management settings');
                                        handleDrawerSave();
                                    }}
                                    className="bg-tabline border-none rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                                >
                                    {t('customerManagement.superAgent.manage.confirm')}
                                </button>
                            </div>
                        </div>
                    );
                })()}
            </Drawer>
        </div>
    );
};

export default SuperAgent;